import { z } from 'zod';
import { COMMS_CHANNELS } from './comms-consent.js';
/**
 * Core comms, part 1: email (engine rollout step 6; `ENGINE-PROTOCOL.md` s7 `core.comms.send` and
 * `core.consent.precheck`; design `docs/CORE-COMMS-EMAIL-DESIGN.md`). Module-to-module routes under
 * `/api/v1/comms-machine`, called with a module's service token and the org header. The engine never
 * sees these bodies: a module renders its message on the platform VM and hands it to core here.
 */
/** Why a message is sent. The purpose decides which consent rule applies (`commsPurposeRule`). */
export const COMMS_PURPOSES = [
    'appointment_reminder',
    'appointment_confirmation',
    'care_notice',
    'marketing',
    'staff_notice',
];
/**
 * The consent kind a purpose is judged by and the consent category whose answer counts
 * (`categoryKey` null: no client category applies, e.g. a notice to a staff member).
 */
export function commsPurposeRule(purpose) {
    switch (purpose) {
        case 'appointment_reminder':
        case 'appointment_confirmation':
            return { kind: 'transactional', categoryKey: 'appointments' };
        case 'care_notice':
            return { kind: 'transactional', categoryKey: 'care' };
        case 'marketing':
            return { kind: 'marketing', categoryKey: 'marketing' };
        case 'staff_notice':
            return { kind: 'transactional', categoryKey: null };
    }
}
/** Why a precheck said no. Enum values only: these travel in responses and in `comms_messages.errorCode`. */
export const COMMS_DENY_REASONS = [
    'no_contact',
    'no_address',
    'suppressed_stop',
    'suppressed_bounce',
    'suppressed_complaint',
    'suppressed_unsubscribe',
    'suppressed_manual',
    'opted_out',
    'not_opted_in',
];
/** The only extra mail headers a caller may set (RFC 8058 one-click unsubscribe, and a reply address). */
export const COMMS_MAIL_HEADERS = [
    'List-Unsubscribe',
    'List-Unsubscribe-Post',
    'Reply-To',
];
const uuid = z.string().uuid();
const headerValue = z
    .string()
    .trim()
    .min(1)
    .max(2000)
    .refine((v) => !/[\r\n]/.test(v), 'a header value cannot contain a line break');
export const commsPrecheckSchema = z
    .object({
    contactId: uuid,
    channel: z.enum(COMMS_CHANNELS),
    purpose: z.enum(COMMS_PURPOSES),
})
    .strict();
export const commsSendSchema = z
    .object({
    /** Parts 1 and 2: email and sms. Voice is the engine's carve-out, never a send here. */
    channel: z.enum(['email', 'sms']),
    toContactId: uuid.optional(),
    toUserId: uuid.optional(),
    /** Email only; required there. */
    subject: z.string().trim().min(1).max(500).optional(),
    /** The plain-text body. Always required: it is the fallback part of a multipart mail. */
    text: z.string().min(1).max(100_000),
    html: z.string().min(1).max(500_000).optional(),
    headers: z.record(z.string(), headerValue).optional(),
    purpose: z.enum(COMMS_PURPOSES),
    /** The calling module's name (`reminders`, `alertshub`, `core`). */
    sourceModule: z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'a module name'),
    /** The caller's own row id for this message (its send row, outbox row, ...). */
    sourceRecordId: z.string().trim().min(1).max(128),
})
    .strict()
    .refine((v) => (v.toContactId ? 1 : 0) + (v.toUserId ? 1 : 0) === 1, {
    message: 'exactly one of toContactId or toUserId',
    path: ['toContactId'],
})
    .refine((v) => !v.headers ||
    Object.keys(v.headers).every((h) => COMMS_MAIL_HEADERS.includes(h)), { message: `headers may only be ${COMMS_MAIL_HEADERS.join(', ')}`, path: ['headers'] })
    .refine((v) => v.channel !== 'email' || !!v.subject, {
    message: 'an email needs a subject',
    path: ['subject'],
})
    .refine((v) => v.channel !== 'sms' || (!v.subject && !v.html && !v.headers), {
    message: 'a text has no subject, html or headers',
    path: ['channel'],
})
    .refine((v) => v.channel !== 'sms' || v.text.length <= 1000, {
    message: 'a text is at most 1000 characters',
    path: ['text'],
});
/**
 * Part 3: what the engine's Inbound workflow posts to `POST /api/v1/engine/inbound/:provider` after
 * verifying the provider's signature and reducing the callback: ids, numbers, enums and a keyword
 * class, never the message text (`ENGINE-PROTOCOL.md` s10; credify-platform
 * `engine/workflows/engine-inbound-telnyx.json`).
 */
export const COMMS_INBOUND_KINDS = [
    'stop',
    'start',
    'reply',
    'sent',
    'delivered',
    'failed',
    'unconfirmed',
];
export const COMMS_KEYWORD_CLASSES = ['STOP', 'START', 'C', 'R', 'HELP', 'OTHER'];
const phone = z
    .string()
    .trim()
    .regex(/^\+?\d{7,15}$/, 'a phone number in E.164 form');
const short = (max) => z.string().trim().min(1).max(max);
export const commsInboundSchema = z
    .object({
    provider: z.literal('telnyx'),
    channel: z.literal('sms'),
    /** The provider's event id: Telnyx retries carry the same one, so it is the idempotency key. */
    eventId: short(128),
    eventType: short(64),
    at: z.string().trim().max(40).optional(),
    providerMessageId: short(128).optional(),
    messagingProfileId: short(128).optional(),
    /** The sender (a client, on a reply) or the practice number (on a receipt). */
    from: phone.optional(),
    /** The practice number (on a reply) or the client (on a receipt). */
    to: phone.optional(),
    kind: z.enum(COMMS_INBOUND_KINDS),
    keywordClass: z.enum(COMMS_KEYWORD_CLASSES).optional(),
    status: z.string().trim().max(40).optional(),
    errorCode: z.string().trim().max(40).optional(),
})
    .strict();
//# sourceMappingURL=comms.js.map